/* istanbul ignore file */
import { Show } from 'framework/show';
import { ShowSection, SectionRow, BigSectionRow, ShowPageLayout } from 'framework/ui/layout';
import { TextField } from 'framework/ui/field';
import useJournalEntryService from 'domain/finance/journalEntry/useJournalEntryService';
import JournalEntryHeader from 'domain/finance/journalEntry/shared/JournalEntryHeader';
import CopyJournalEntryButton from 'domain/finance/journalEntry/shared/button/CopyJournalEntryButton';
import EditJournalEntryButton from 'domain/finance/journalEntry/shared/button/EditJournalEntryButton';
import ReverseJournalEntryButton from 'domain/finance/journalEntry/shared/button/ReverseJournalEntryButton';
import SingleJournalEntryBreadcrumbs from 'domain/finance/journalEntry/shared/SingleJournalEntryBreadcrumbs';
import { useJournalEntryId, useTransferId } from 'domain/finance/journalEntry/shared/useJournalEntryRouting';
import JournalEntryTransfersList from '../shared/field/JournalEntryTransfersList';
import JournalEntryLink from '../shared/field/JournalEntryLink';
import { OfficeCodeField, EmployeeLinkField } from 'domain/shared/employee';
import JournalEntryAttributesList from 'domain/finance/journalEntry/shared/field/JournalEntryAttributesList';
import JournalEntryBussinessObjectField from 'domain/finance/journalEntry/shared/field/JournalEntryBussinessObjectField';
import InvoiceLink from 'domain/finance/invoice/shared/field/InvoiceLink';
import { AccountLinkField } from 'domain/shared/account';
import { ProjectLinkField, READ_PROJECTS_LIST } from 'domain/shared/project';
import LeaveLink from 'domain/shared/leave/field/LeaveLink';

const JournalEntryViewPage = ({ shouldSearchByTransferId }: { shouldSearchByTransferId?: boolean }) => {
  const { getById, getJournalEntryByTransferId } = useJournalEntryService();
  const id = useJournalEntryId();
  const transferId = useTransferId();

  return (
    <Show
      key={`journal-entry-${id}`}
      getDataMethod={() => (shouldSearchByTransferId ? getJournalEntryByTransferId(transferId) : getById(id))}
    >
      <ShowPageLayout
        entityHeader={<JournalEntryHeader />}
        breadcrumbs={<SingleJournalEntryBreadcrumbs />}
        headerButtons={[<EditJournalEntryButton />, <CopyJournalEntryButton />, <ReverseJournalEntryButton />]}
        hasSeparateSections
      >
        <ShowSection>
          <SectionRow>
            <TextField label="Date" source="asDateOf" />
            <OfficeCodeField source="attributes.office" />
            <JournalEntryLink
              label="Original journal entry"
              source="attributes.revertedOriginalJournalEntryId"
              isVisible={(data: any) => !!data.getValueBySource('attributes')?.revertedOriginalJournalEntryId}
            />
            <JournalEntryBussinessObjectField
              source="attributes.businessObjectId"
              typeSource="attributes.businessObjectType"
              isVisible={(data: any) => !!data.getValueBySource('attributes')?.businessObjectType}
            />
            <InvoiceLink
              label="Invoice"
              source="attributes.invoiceId"
              nameSource="attributes.invoiceId"
              isVisible={(data: any) => {
                const attributes = data.getValueBySource('attributes') || {};

                return attributes.invoiceId && attributes.businessObjectType !== 'INVOICE';
              }}
            />
            <LeaveLink
              label="Leave"
              source="attributes.leaveId"
              nameSource="attributes.leaveId"
              isVisible={(data: any) => data.getValueBySource('attributes')?.leaveId}
            />
            <EmployeeLinkField
              label="Worker"
              source="attributes.employeeAlias"
              nameSource="attributes.employeeName"
              isVisible={(data: any) => data.getValueBySource('attributes')?.employeeAlias}
            />
            <ProjectLinkField
              source="attributes.projectId"
              nameSource="attributes.projectId"
              label="Project"
              isVisible={(data: any) => data.getValueBySource('attributes')?.projectId}
              permissionToCheck={READ_PROJECTS_LIST}
            />
            <AccountLinkField
              label="Account"
              source="attributes.accountId"
              idSource="attributes.accountId"
              isInternal
              idValue={undefined}
              isVisible={(data: any) => data.getValueBySource('attributes')?.accountId}
            />
            <TextField
              source="attributes.sowId"
              label="SOW number"
              isVisible={(data: any) => data.getValueBySource('attributes')?.sowId}
            />
          </SectionRow>
          <BigSectionRow>
            <TextField label="Description" source="description" />
          </BigSectionRow>
        </ShowSection>
        <ShowSection title="Transfers">
          <JournalEntryTransfersList source="transfers" />
        </ShowSection>
        <ShowSection title="Attributes">
          <JournalEntryAttributesList source="attributes" />
        </ShowSection>
      </ShowPageLayout>
    </Show>
  );
};

export default JournalEntryViewPage;
